/**
 * googleTranslateManager.js
 *
 * Singleton that owns the ONE Google Translate instance for the whole app.
 * All <GoogleTranslate> components call into this module — they never touch
 * the GT API or the hidden host div directly.
 *
 * Why a singleton?
 * ─────────────────
 * Google Translate can only be initialised once per page load into ONE div.
 * If two React components both try to call `new TranslateElement(...)` they
 * fight over the combo-box, producing the "select English first" workaround
 * you discovered.  Extracting init into a module-level singleton means it
 * runs exactly once no matter how many <GoogleTranslate> components are on
 * screen (Header desktop + Header mobile sidebar + Accounts page).
 */

// ─── Module-level state (shared across all imports) ─────────────────────────
let _initialized = false;
let _readyCallbacks = [];   // functions waiting for GT to be ready
let _isGtReady = false;

// ─── Cookie helpers ──────────────────────────────────────────────────────────
export const getGoogTransCookie = () => {
  const match = document.cookie.match(/(?:^|;\s*)googtrans=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
};

export const setGoogTransCookie = (value) => {
  document.cookie = `googtrans=${value};path=/`;
};

export const clearGoogTransCookie = () => {
  const hostname = window.location.hostname;
  const paths = ["/", window.location.pathname];
  const domains = [
    "",
    hostname,
    `.${hostname}`
  ];

  const hostParts = hostname.split('.');
  if (hostParts.length >= 2) {
    const parentDomain = hostParts.slice(-2).join('.');
    domains.push(parentDomain);
    domains.push(`.${parentDomain}`);
  }

  domains.forEach((domain) => {
    paths.forEach((path) => {
      let cookieStr = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 GMT";
      if (path) cookieStr += `; path=${path}`;
      if (domain) cookieStr += `; domain=${domain}`;
      document.cookie = cookieStr;
    });
  });
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Fire all queued "GT is ready" callbacks, then drain the queue. */
const _fireReady = () => {
  _isGtReady = true;
  _readyCallbacks.forEach((cb) => cb());
  _readyCallbacks = [];
};

/**
 * Run `callback` once the hidden .goog-te-combo select exists.
 * If GT is already ready, runs immediately (via setTimeout to stay async).
 */
export const onGtReady = (callback) => {
  if (_isGtReady) {
    setTimeout(callback, 0);
  } else {
    _readyCallbacks.push(callback);
  }
};

// ─── Core: trigger translation ───────────────────────────────────────────────

/**
 * Set the hidden GT combo to `languageCode` and dispatch a change event.
 * Retries until the combo appears (handles cases where GT hasn't rendered yet).
 */
export const triggerTranslate = (languageCode, retryCount = 0) => {
  const MAX_RETRIES = 20;
  const RETRY_DELAY = 300;

  const select = document.querySelector(".goog-te-combo");
  if (select) {
    select.value = languageCode;
    select.dispatchEvent(new Event("change", { bubbles: true }));
  } else if (retryCount < MAX_RETRIES) {
    setTimeout(() => triggerTranslate(languageCode, retryCount + 1), RETRY_DELAY);
  } else {
    console.error("[GT Manager] .goog-te-combo not found after retries.");
  }
};

// ─── Restore saved language (called after GT initialises) ────────────────────
const _restoreSavedLanguage = () => {
  // If this load was triggered by our own "reset to English" reload, skip.
  if (sessionStorage.getItem("gt_reloading")) {
    sessionStorage.removeItem("gt_reloading");
    return;
  }

  const saved = localStorage.getItem("selectedLanguage");
  if (!saved || saved === "en") {
    clearGoogTransCookie();
    return;
  }

  // Keep cookie in sync with localStorage
  const cookie = getGoogTransCookie();
  const cookieLang = cookie ? cookie.split("/").pop() : null;
  if (!cookieLang || cookieLang === "en" || cookieLang !== saved) {
    setGoogTransCookie(`/en/${saved}`);
  }

  triggerTranslate(saved);
};

// ─── Ensure the hidden host div exists ───────────────────────────────────────
const _ensureHostDiv = () => {
  if (document.getElementById("google_translate_element")) return;

  const div = document.createElement("div");
  div.id = "google_translate_element";
  Object.assign(div.style, {
    position: "fixed",
    bottom: "0",
    right: "0",
    opacity: "0.01",
    pointerEvents: "none",
    zIndex: "-1",
  });
  document.body.appendChild(div);
};

// ─── Main init ───────────────────────────────────────────────────────────────

/**
 * Call this from any component that needs GT.
 * Safe to call multiple times — only runs once per page load.
 */
export const initGoogleTranslate = () => {
  if (_initialized) return;
  _initialized = true;

  _ensureHostDiv();

  const _doInit = () => {
    if (!window.google?.translate) return;

    try {
      new window.google.translate.TranslateElement(
        {
          pageLanguage: "en",
          autoDisplay: false,
          includedLanguages: "hi,gu,mr,ta,te,kn,ml,bn,pa,es,fr,de,ar,ru,ja,ko,it,pt",
        },
        "google_translate_element"
      );

      // Give the combo box time to render, then fire ready callbacks
      setTimeout(() => {
        _fireReady();
        _restoreSavedLanguage();
      }, 800);
    } catch (err) {
      console.error("[GT Manager] Init error:", err);
      // Reset so next poll attempt can retry
      _initialized = false;
    }
  };

  // Expose the callback GT script expects
  window.googleTranslateElementInit = _doInit;

  if (!document.getElementById("google-translate-script")) {
    const script = document.createElement("script");
    script.id = "google-translate-script";
    script.src =
      "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    script.async = true;
    document.body.appendChild(script);
  } else if (window.google?.translate) {
    // Script already loaded and GT available — init immediately
    _doInit();
  }

  // Polling fallback: if script loads but callback already fired before our
  // window.googleTranslateElementInit was set, catch it here.
  const poll = setInterval(() => {
    if (window.google?.translate) {
      clearInterval(poll);
      if (!_isGtReady) _doInit();
    }
  }, 400);
};

// ─── Public language-change action ───────────────────────────────────────────

/**
 * Change the page language.
 * @param {string} languageCode  e.g. "hi", "en"
 * @param {function} onSelect    optional callback(code) so the component can update its UI
 */
export const changeLanguage = (languageCode, onSelect) => {
  localStorage.setItem("selectedLanguage", languageCode);
  if (onSelect) onSelect(languageCode);

  // Dispatch global custom event for other instances
  window.dispatchEvent(new CustomEvent("languageChanged", { detail: languageCode }));

  if (languageCode === "en") {
    clearGoogTransCookie();
    sessionStorage.setItem("gt_reloading", "1");
    window.location.reload();
    return;
  }

  setGoogTransCookie(`/en/${languageCode}`);

  // If GT is ready, fire immediately; otherwise queue it
  onGtReady(() => triggerTranslate(languageCode));
};